/*
 * KAYA — "Complete the look" cross-sell card.
 *
 * Markup + a JSON colour map are server-rendered by snippets/kaya-complete-look.liquid.
 * This script keeps the card's partner product in sync with the main product's
 * currently selected colour, and wires the "+ Add" pill to /cart/add.js.
 *
 * SIZE RULE: target the partner variant in the current colour at the currently
 * selected size IF that size is available in that colour, otherwise the first
 * available size in that colour. (No size UI in the card — CSB has none.)
 *
 * Every variant id / price / image comes from the Liquid JSON; nothing is
 * hard-coded here. Null-guarded throughout so PDPs without the block never crash.
 */
(() => {
  const wrapper = document.querySelector('[data-kaya-complete-look]');
  if (!wrapper) return;

  const dataEl = wrapper.querySelector('.kaya-complete-look__data');
  let MAP = null;
  try {
    MAP = JSON.parse(dataEl.textContent);
  } catch (e) {
    return; // malformed / missing JSON — bail silently
  }
  if (!MAP || typeof MAP !== 'object') return;

  const productRoot = document.querySelector('.product') || document;
  const imgEl = wrapper.querySelector('.kaya-complete-look__img');
  const titleEl = wrapper.querySelector('.kaya-complete-look__title');
  const priceEl = wrapper.querySelector('.kaya-complete-look__price');
  const addBtn = wrapper.querySelector('.kaya-complete-look__add');
  if (!addBtn) return;

  const ADD_LABEL = '+ Add';
  const normalize = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

  // Currently selected colour / size on the MAIN product (radio button pickers).
  const checkedValue = (re) => {
    const el = productRoot.querySelector(
      `input[type="radio"][name*="${re}"]:checked, input[type="radio"][name*="${re}"][checked]`
    );
    return el ? el.value : '';
  };
  const currentColor = () => normalize(checkedValue('Color') || checkedValue('Colour'));
  const currentSize = () => checkedValue('Size'); // keep original case (matches map keys)

  // Pick the partner size variant per the SIZE RULE; returns the size entry or null.
  const pickSizeEntry = (entry) => {
    const sizes = entry.sizes || {};
    const wanted = currentSize();
    if (wanted && sizes[wanted] && sizes[wanted].available) return sizes[wanted];
    // else: first available size in this colour
    for (const key of Object.keys(sizes)) {
      if (sizes[key] && sizes[key].available) return sizes[key];
    }
    return null;
  };

  const update = () => {
    const color = currentColor();
    const entry = (color && MAP[color]) || MAP[Object.keys(MAP)[0]];
    if (!entry) return;

    if (imgEl) {
      if (entry.img300) imgEl.src = entry.img300;
      if (entry.img300 && entry.img600) {
        imgEl.srcset = `${entry.img300} 300w, ${entry.img600} 600w`;
      }
      if (entry.title) imgEl.alt = entry.title;
    }
    if (titleEl && entry.title) titleEl.textContent = entry.title;

    const sizeEntry = pickSizeEntry(entry);
    if (sizeEntry && sizeEntry.id != null) {
      addBtn.dataset.variantId = String(sizeEntry.id);
      addBtn.disabled = false;
      if (addBtn.dataset.state !== 'added') {
        addBtn.textContent = ADD_LABEL;
        // Name the partner so the terse "+ Add" pill isn't ambiguous to screen readers.
        addBtn.setAttribute('aria-label', entry.title ? `Add ${entry.title} to cart` : ADD_LABEL);
      }
      if (priceEl && sizeEntry.price) priceEl.textContent = sizeEntry.price;
    } else {
      // No available variant in this colour.
      delete addBtn.dataset.variantId;
      addBtn.disabled = true;
      addBtn.textContent = 'Sold out';
      addBtn.setAttribute('aria-label', entry.title ? `${entry.title} is sold out` : 'Sold out');
    }
  };

  // --- Add to cart -----------------------------------------------------------
  const onAdd = () => {
    const variantId = addBtn.dataset.variantId;
    if (!variantId || addBtn.dataset.state === 'adding') return;

    addBtn.dataset.state = 'adding';

    const cart =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
      formData.append('sections_url', window.location.pathname);
    }

    // Prefer the theme helper (assets/global.js); drop Content-Type so the
    // browser sets the multipart boundary for FormData.
    let config;
    if (typeof fetchConfig === 'function') {
      config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
    } else {
      config = {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      };
    }
    config.body = formData;

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(addUrl, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Shopify returns a status on error (e.g. sold out).
          throw new Error(response.description || response.message || 'add failed');
        }
        if (
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'kaya-complete-look',
            productVariantId: variantId,
            cartData: response,
          });
        }
        if (cart && typeof cart.renderContents === 'function') {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(response);
        }
        addBtn.dataset.state = 'added';
        addBtn.textContent = 'Added ✓';
        addBtn.setAttribute('aria-label', 'Added to cart');
        setTimeout(() => {
          delete addBtn.dataset.state;
          update(); // restores label / variant id for current selection
        }, 1600);
      })
      .catch((e) => {
        delete addBtn.dataset.state;
        update();
        console.error('[kaya-complete-look]', e);
      });
  };

  addBtn.addEventListener('click', onAdd);

  // --- Keep in sync with variant changes ------------------------------------
  // Primary: the theme's pub/sub (fires after the variant picker settles).
  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS.variantChange
  ) {
    subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(update, 0));
  }
  // Fallback: delegated change listener on the main product's inputs.
  productRoot.addEventListener('change', (e) => {
    const t = e.target;
    if (t && t.name && /Color|Colour|Size/i.test(t.name)) update();
  });

  update();
})();
